class ConnectedContentCarousel extends HTMLElement {
  constructor() {
    super();

    this.activeIndex = 0;
    this.slideCount = 0;
    this.progressAnimation = null;
    this.pausedReasons = new Set();
    this.pointerStart = null;
    this.initialized = false;

    this.handleClick = this.handleClick.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handlePointerCancel = this.handlePointerCancel.bind(this);
    this.handleFocusIn = this.handleFocusIn.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleMotionPreference = this.handleMotionPreference.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleBlockDeselect = this.handleBlockDeselect.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.slides = Array.from(this.querySelectorAll('[data-carousel-slide]'));
    this.segments = Array.from(this.querySelectorAll('[data-carousel-go]'));
    this.progressFills = Array.from(this.querySelectorAll('[data-carousel-progress]'));
    this.controls = this.querySelector('[data-carousel-controls]');
    this.viewport = this.querySelector('[data-carousel-viewport]');
    this.previousButton = this.querySelector('[data-carousel-previous]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.slideCount = this.slides.length;

    if (!this.slideCount) return;

    this.duration = Math.max(1000, Number.parseInt(this.dataset.duration, 10) || 6000);
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.loopEnabled = this.dataset.loop !== 'false';
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));

    this.addEventListener('click', this.handleClick);
    this.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
    this.addEventListener('pointerup', this.handlePointerUp, { passive: true });
    this.addEventListener('pointercancel', this.handlePointerCancel, { passive: true });
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);

    if (this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener('change', this.handleMotionPreference);
    } else {
      this.motionQuery.addListener(this.handleMotionPreference);
    }

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.2) {
            this.resume('offscreen');
          } else {
            this.pause('offscreen');
          }
        },
        { threshold: [0, 0.2] },
      );
      this.observer.observe(this);
    }

    if (document.hidden) this.pausedReasons.add('document-hidden');

    this.slides.forEach((slide) => {
      slide.hidden = false;
    });
    this.classList.add('is-ready');

    if (this.controls && this.slideCount > 1) {
      this.controls.hidden = false;
    }

    this.applyActiveState();
    this.resetProgress();
    this.initialized = true;
  }

  disconnectedCallback() {
    this.cancelProgress();
    this.observer?.disconnect();

    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('pointerdown', this.handlePointerDown);
    this.removeEventListener('pointerup', this.handlePointerUp);
    this.removeEventListener('pointercancel', this.handlePointerCancel);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);

    if (this.motionQuery?.removeEventListener) {
      this.motionQuery.removeEventListener('change', this.handleMotionPreference);
    } else {
      this.motionQuery?.removeListener(this.handleMotionPreference);
    }

    this.initialized = false;
  }

  get canAutoplay() {
    const hasNextSlide = this.loopEnabled || this.activeIndex < this.slideCount - 1;
    return this.autoplayEnabled && this.slideCount > 1 && hasNextSlide && !this.motionQuery.matches;
  }

  handleClick(event) {
    if (event.target.closest('[data-carousel-previous]')) {
      this.showSlide(this.activeIndex - 1);
      return;
    }

    if (event.target.closest('[data-carousel-next]')) {
      this.showSlide(this.activeIndex + 1);
      return;
    }

    const segment = event.target.closest('[data-carousel-go]');
    if (segment) this.showSlide(Number.parseInt(segment.dataset.carouselGo, 10));
  }

  handlePointerDown(event) {
    this.resume('focus');

    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    if (event.target.closest('a, button')) return;

    this.pointerStart = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
    };
  }

  handlePointerUp(event) {
    if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;

    const deltaX = event.clientX - this.pointerStart.x;
    const deltaY = event.clientY - this.pointerStart.y;
    this.pointerStart = null;

    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.25) return;

    const isRtl = window.getComputedStyle(this).direction === 'rtl';
    if ((deltaX < 0 && !isRtl) || (deltaX > 0 && isRtl)) {
      this.showSlide(this.activeIndex + 1);
    } else {
      this.showSlide(this.activeIndex - 1);
    }
  }

  handlePointerCancel() {
    this.pointerStart = null;
  }

  handleFocusIn(event) {
    if (event.target.matches?.(':focus-visible')) {
      this.pause('focus');
    } else {
      this.resume('focus');
    }
  }

  handleFocusOut(event) {
    if (!event.relatedTarget || !this.contains(event.relatedTarget)) {
      this.resume('focus');
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.pause('document-hidden');
    } else {
      this.resume('document-hidden');
    }
  }

  handleMotionPreference() {
    this.resetProgress();
  }

  handleBlockSelect(event) {
    const selectedBlock = event.target.closest?.('[data-block-id]');
    if (!selectedBlock || !this.contains(selectedBlock)) return;

    const index = this.slides.indexOf(selectedBlock);
    if (index < 0) return;

    this.pause('theme-editor');
    this.showSlide(index);
  }

  handleBlockDeselect(event) {
    const selectedBlock = event.target.closest?.('[data-block-id]');
    if (selectedBlock && this.contains(selectedBlock)) this.resume('theme-editor');
  }

  showSlide(requestedIndex) {
    if (!Number.isFinite(requestedIndex) || !this.slideCount) return;

    if (this.loopEnabled) {
      this.activeIndex = (requestedIndex + this.slideCount) % this.slideCount;
    } else {
      this.activeIndex = Math.min(Math.max(requestedIndex, 0), this.slideCount - 1);
    }

    this.applyActiveState();
    this.resetProgress();
  }

  applyActiveState() {
    this.slides.forEach((slide, index) => {
      const isActive = index === this.activeIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.inert = !isActive;
    });

    this.segments.forEach((segment, index) => {
      const isActive = index === this.activeIndex;
      segment.classList.toggle('is-active', isActive);
      if (isActive) {
        segment.setAttribute('aria-current', 'true');
      } else {
        segment.removeAttribute('aria-current');
      }
    });

    if (!this.loopEnabled) {
      if (this.previousButton) this.previousButton.disabled = this.activeIndex === 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex === this.slideCount - 1;
    }
  }

  resetProgress() {
    this.cancelProgress();

    this.progressFills.forEach((fill, index) => {
      fill.getAnimations().forEach((animation) => animation.cancel());
      const isActive = index === this.activeIndex;
      fill.style.transform = `translateY(-50%) scaleX(${isActive && !this.canAutoplay ? 1 : 0})`;
    });

    if (!this.canAutoplay) return;

    const activeFill = this.progressFills[this.activeIndex];
    if (!activeFill) return;

    this.progressAnimation = activeFill.animate(
      [
        { transform: 'translateY(-50%) scaleX(0)' },
        { transform: 'translateY(-50%) scaleX(1)' },
      ],
      {
        duration: this.duration,
        easing: 'linear',
        fill: 'forwards',
      },
    );

    this.progressAnimation.onfinish = () => {
      this.progressAnimation = null;
      this.showSlide(this.activeIndex + 1);
    };

    if (this.pausedReasons.size) this.progressAnimation.pause();
  }

  cancelProgress() {
    if (!this.progressAnimation) return;

    this.progressAnimation.onfinish = null;
    this.progressAnimation.cancel();
    this.progressAnimation = null;
  }

  pause(reason) {
    this.pausedReasons.add(reason);
    this.progressAnimation?.pause();
  }

  resume(reason) {
    this.pausedReasons.delete(reason);
    if (this.pausedReasons.size || !this.canAutoplay) return;

    if (this.progressAnimation) {
      this.progressAnimation.play();
    } else {
      this.resetProgress();
    }
  }
}

if (!customElements.get('connected-content-carousel')) {
  customElements.define('connected-content-carousel', ConnectedContentCarousel);
}
